import { FlyoutListMenuWidget, MenuButtonWidget } from '@tableau/widgets';
import React from 'react';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import * as TypeStyle from 'typestyle';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { Icons } from '~src/commonInfra/components/Icons';
import { PackageDetailsSubspace } from '~src/packages/components/PackageDetailsSubspace';
import { IPackage } from '~src/packages/models/IPackage';
import { ListPackagesService } from '~src/packages/serverApi/requests/ListPackages';
import { SetUserPackagePreferenceService } from '~src/packages/serverApi/requests/SetUserPackagePreference';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { Subspace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';

export interface SiteUserPackagesSubspaceContext {
  readonly username: string;
  readonly domainName: string;
  readonly urls: SpacesSiteUrls;
}

// Per-user "Packages" tab on the user page. Lists the packages the calling
// user can see and lets them set their per-user activation preference. The
// admin Settings page is the place to upload, enable, or configure packages;
// this tab is read-only apart from the activation toggle.
export function siteUserPackagesSubspace(context: SiteUserPackagesSubspaceContext): Subspace {
  return {
    name: 'packages',
    tabLabel: Messages.packages_tab_title(),
    shouldRenderTabContentCount: false,
    renderContent: () => <SiteUserPackagesRouter context={context} />
  };
}

function SiteUserPackagesRouter(props: { context: SiteUserPackagesSubspaceContext }): React.ReactElement {
  const { context } = props;
  return (
    <Switch>
      <Route
        exact
        path={decodeURIComponent(context.urls.userPackageDetailsPath(':username+', ':domain', ':luid'))}
        render={(routeProps: RouteComponentProps<{ username: string, domain: string, luid: string }>) => (
          <PackageDetailsSubspace
            {...routeProps.match}
            backHref={context.urls.userPackagesUrl(context.username, context.domainName)}
            showAdminActions={false}
          />
        )}
      />
      <Route render={() => <SiteUserPackagesContent context={context} />} />
    </Switch>
  );
}

function SiteUserPackagesContent(props: { context: SiteUserPackagesSubspaceContext }): React.ReactElement {
  const { context } = props;
  const [packages, setPackages] = React.useState<IPackage[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const loadPackages = React.useCallback(() => {
    setPackages(null);
    setLoadError(null);
    return ListPackagesService.listPackages()
      .then(response => setPackages(response.packages?.package ?? []))
      .catch((err: { data?: { error?: { detail?: string } } }) => {
        setLoadError(err?.data?.error?.detail ?? 'Failed to load packages');
      });
  }, []);

  React.useEffect(() => { loadPackages(); }, [loadPackages]);

  return (
    <div style={rootStyle}>
      <PackagesContent packages={packages} loadError={loadError} context={context} reload={loadPackages} />
    </div>
  );
}

function PackagesContent(props: {
  packages: IPackage[] | null;
  loadError: string | null;
  context: SiteUserPackagesSubspaceContext;
  reload: () => Promise<void>;
}): React.ReactElement {
  const { packages, loadError, context, reload } = props;

  if (loadError) {
    return <div className="tb-warning-text">{loadError}</div>;
  }
  if (packages === null) {
    return <div>{'Loading...'}</div>;
  }
  if (packages.length === 0) {
    return <div>{Messages.packages_empty()}</div>;
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={cellStyle}>{'Name'}</th>
          <th style={cellStyle}>{'Status'}</th>
          <th style={cellStyle}>{'Updated'}</th>
          <th style={cellStyle}>{'Actions'}</th>
        </tr>
      </thead>
      <tbody>
        {packages.map(pkg => (
          <tr key={pkg.luid}>
            <td style={cellStyle}><PackageNameLink pkg={pkg} context={context} /></td>
            <td style={cellStyle}>{statusLabel(pkg)}</td>
            <td style={cellStyle}>{pkg.lastUpdatedAt ?? pkg.createdAt ?? ''}</td>
            <td style={cellStyle}><PackageRowActions pkg={pkg} reload={reload} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PackageRowActions(props: { pkg: IPackage, reload: () => Promise<void> }): JSX.Element {
  const { pkg, reload } = props;
  const buttonRef = React.useRef<HTMLButtonElement>();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const close = React.useCallback(() => setIsOpen(false), []);
  const toggle = React.useCallback(() => setIsOpen(o => !o), []);

  // Admin kill-switch wins; the activation toggle is disabled when off.
  const adminDisabled = pkg.enabled === false;
  const isActive = isActiveForUser(pkg);

  const handleSelect = React.useCallback((value: string) => {
    setIsOpen(false);
    if (value === 'toggleActivation' && pkg.luid) {
      SetUserPackagePreferenceService.setUserPackagePreference(pkg.luid, !isActive)
        .then(() => reload())
        .catch((err: { data?: { error?: { detail?: string } } }) => {
          ToasterService.instance.pop({
            type: ToastType.Error,
            message: err?.data?.error?.detail ?? 'Failed to update package preference',
          });
        });
    }
  }, [pkg, isActive, reload]);

  if (!pkg.luid) {
    return <span>{'—'}</span>;
  }

  const menuItemInfos = [
    {
      value: 'toggleActivation',
      displayValue: isActive ? 'Deactivate' : 'Activate',
      disabled: adminDisabled,
    },
  ];

  const flyoutId = `user-packages-row-actions-flyout-${pkg.luid}`;

  return (
    <span className={inlineBlockClass}>
      <MenuButtonWidget
        aria-controls={flyoutId}
        aria-label={Messages.moreActions()}
        className={moreActionsButtonStyleName}
        onClick={toggle}
        ref={buttonRef}>
        <Icons.actionIcon />
      </MenuButtonWidget>
      {isOpen && (
        <FlyoutListMenuWidget
          allowAutoPosition
          anchorEl={buttonRef.current}
          id={flyoutId}
          menuItemInfos={menuItemInfos}
          menuStyle={{ minWidth: 144 }}
          onSelect={handleSelect}
          requestCloseAll={close}
          toggleEl={buttonRef.current} />
      )}
    </span>
  );
}

function statusLabel(pkg: IPackage): string {
  if (pkg.enabled === false) {
    return 'Admin disabled';
  }
  if (pkg.userPreference === false) {
    return 'You opted out';
  }
  if (isActiveForUser(pkg)) {
    return 'Active';
  }
  return 'Inactive';
}

// Mirrors the server's runtime activation predicate.
function isActiveForUser(pkg: IPackage): boolean {
  if (pkg.enabled === false) {
    return false;
  }
  if (pkg.userPreference !== undefined) {
    return pkg.userPreference;
  }
  return pkg.defaultActivation === true;
}

function PackageNameLink(props: { pkg: IPackage, context: SiteUserPackagesSubspaceContext }): JSX.Element {
  const { pkg, context } = props;
  const display = pkg.name ?? pkg.manifestId ?? '';
  if (!pkg.luid) {
    return <span>{display}</span>;
  }
  return <a href={context.urls.userPackageDetailsUrl(context.username, context.domainName, pkg.luid)}>{display}</a>;
}

const rootStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  overflow: 'auto',
  padding: '24px',
};

const cellStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderBottom: '1px solid #ddd',
  textAlign: 'left',
};

const inlineBlockClass = TypeStyle.style({
  display: 'inline-block',
});

const moreActionsButtonStyleName = TypeStyle.style({
  border: 'none',
  verticalAlign: 'middle',
  $nest: {
    '&&': { height: 20, width: 20, minHeight: 20, minWidth: 20, padding: 0 },
    '&&::after': { display: 'none' },
  },
});
